import { THEME_PRESETS, DEFAULT_THEME } from './constants';

const hexToRgb = (hex) => {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const int = parseInt(h || '000000', 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

const isHexColor = (value) => /^#[0-9a-f]{6}$/i.test(String(value || '').trim());

const colorOr = (value, fallback) =>
  isHexColor(value) ? String(value).trim() : fallback;

export const rgba = (hex, a) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// Cor de texto legível sobre um fundo (preto ou branco), via luminância.
export const inkOn = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? '#101a2b' : '#ffffff';
};

const isDarkColor = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.55;
};

const getPreset = (presetId) =>
  THEME_PRESETS.find((p) => p.id === presetId) || THEME_PRESETS[0];

const resolveButtonColors = ({ style, dark, accent, accentInk, text, muted }) => {
  if (style === 'solid') {
    return {
      bg: accent,
      border: 'transparent',
      text: accentInk,
      sub: rgba(accentInk, 0.72),
      badgeBg: accentInk === '#ffffff' ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.9)',
      badgeBorder: 'rgba(0,0,0,0.06)',
      arrow: rgba(accentInk, 0.8),
      shadow: `0 8px 20px ${rgba(accent, 0.28)}`,
    };
  }

  if (style === 'outline') {
    return {
      bg: dark ? 'rgba(255,255,255,0.02)' : rgba(accent, 0.03),
      border: rgba(accent, dark ? 0.75 : 0.55),
      text,
      sub: muted,
      badgeBg: rgba(accent, dark ? 0.18 : 0.12),
      badgeBorder: rgba(accent, 0.3),
      arrow: accent,
      shadow: 'none',
    };
  }

  return {
    bg: dark ? 'rgba(255,255,255,0.06)' : rgba(accent, 0.09),
    border: dark ? 'rgba(255,255,255,0.10)' : rgba(accent, 0.16),
    text,
    sub: muted,
    badgeBg: dark ? 'rgba(255,255,255,0.12)' : '#ffffff',
    badgeBorder: dark ? 'rgba(255,255,255,0.14)' : rgba(accent, 0.16),
    arrow: accent,
    shadow: dark ? 'none' : `0 4px 12px ${rgba('#0e1b2a', 0.05)}`,
  };
};

const fallbackButtonBg = (style, dark, accent) => {
  if (style === 'solid') return accent;
  if (style === 'outline') return dark ? '#162842' : '#ffffff';
  return dark ? '#21344e' : '#eef4fb';
};

const fallbackButtonBorder = (style, dark, accent) => {
  if (style === 'solid' || style === 'outline') return accent;
  return dark ? '#32455f' : '#dbe4f0';
};

const coverGradient = (start, end) =>
  `linear-gradient(150deg, ${start} 0%, ${end} 100%)`;

const paletteCover = (palette, fallback) => {
  const colors = Array.isArray(palette)
    ? [...new Set(palette.filter(isHexColor).map((color) => String(color).trim()))]
    : [];
  if (!colors.length) return fallback;
  if (colors.length === 1) return coverGradient(colors[0], colors[0]);
  const stops = colors.map((color, index) => {
    const position = Math.round((index / (colors.length - 1)) * 100);
    return `${color} ${position}%`;
  });
  return `linear-gradient(135deg, ${stops.join(', ')})`;
};

export const buildCustomColorsFromTheme = (theme = DEFAULT_THEME) => {
  const preset = getPreset(theme.presetId);
  const style = theme.buttonStyle || preset.defaultButtonStyle;
  const accent = colorOr(theme.accent, preset.defaultAccent);
  const dark = preset.mode === 'dark';
  const accentInk = inkOn(accent);
  const base = {
    enabled: false,
    pageBg: preset.pageBg,
    cardBg: preset.cardBg,
    coverStart: preset.coverStart || accent,
    coverEnd: preset.coverEnd || preset.pageBg,
    text: preset.text,
    muted: preset.muted,
    divider: dark ? '#31435f' : '#e2e8f0',
    infoChip: dark ? '#22344d' : '#eef2f8',
    accent,
    accentInk,
    logoRing: preset.logoRing,
    buttonBg: fallbackButtonBg(style, dark, accent),
    buttonBorder: fallbackButtonBorder(style, dark, accent),
    buttonText: style === 'solid' ? accentInk : preset.text,
    buttonSub: style === 'solid'
      ? (accentInk === '#ffffff' ? '#f5f7fb' : '#334155')
      : preset.muted,
    iconBg: dark ? '#263955' : '#ffffff',
    iconBorder: dark ? '#40516c' : '#d8e1ed',
    arrow: accent,
  };
  const custom = theme.customColors || {};

  return Object.keys(base).reduce((acc, key) => {
    if (key === 'enabled') {
      acc.enabled = custom.enabled === true;
      return acc;
    }
    acc[key] = colorOr(custom[key], base[key]);
    return acc;
  }, {});
};

const buildResolvedTheme = ({ preset, mode, pageBg, cover, accent, accentInk, btn, infoChip }) => {
  const vars = {
    '--v-card-bg': preset.cardBg,
    '--v-text': preset.text,
    '--v-muted': preset.muted,
    '--v-divider': preset.divider,
    '--v-accent': accent,
    '--v-accent-ink': accentInk,
    '--v-logo-ring': preset.logoRing,
    '--v-btn-bg': btn.bg,
    '--v-btn-border': btn.border,
    '--v-btn-text': btn.text,
    '--v-btn-sub': btn.sub,
    '--v-btn-shadow': btn.shadow,
    '--v-badge-bg': btn.badgeBg,
    '--v-badge-border': btn.badgeBorder,
    '--v-arrow': btn.arrow,
    '--v-info-chip': infoChip,
  };

  return {
    vars,
    cover,
    pageBg,
    mode,
    accent,
    preset,
  };
};

/**
 * Resolve um objeto de tema { presetId, accent, buttonStyle, customColors } em
 * variáveis CSS consumidas por LinksPageView, além de metadados úteis para o
 * shell público.
 */
export const resolveTheme = (theme = DEFAULT_THEME) => {
  const preset = getPreset(theme.presetId);
  const style = theme.buttonStyle || preset.defaultButtonStyle;
  const usesCustomColors = theme.customColors?.enabled === true;

  if (usesCustomColors) {
    const custom = buildCustomColorsFromTheme(theme);
    const mode = isDarkColor(custom.cardBg) ? 'dark' : 'light';
    const accent = custom.accent;
    const accentInk = custom.accentInk;
    const customPreset = {
      ...preset,
      mode,
      pageBg: custom.pageBg,
      cardBg: custom.cardBg,
      text: custom.text,
      muted: custom.muted,
      divider: custom.divider,
      logoRing: custom.logoRing,
      cover: coverGradient(custom.coverStart, custom.coverEnd),
    };
    const btn = {
      ...resolveButtonColors({
        style,
        dark: mode === 'dark',
        accent,
        accentInk,
        text: custom.text,
        muted: custom.muted,
      }),
      bg: custom.buttonBg,
      border: custom.buttonBorder,
      text: custom.buttonText,
      sub: custom.buttonSub,
      badgeBg: custom.iconBg,
      badgeBorder: custom.iconBorder,
      arrow: custom.arrow,
    };

    return buildResolvedTheme({
      preset: customPreset,
      mode,
      pageBg: custom.pageBg,
      cover: theme.cleanCover
        ? customPreset.cover
        : paletteCover(theme.palette, customPreset.cover),
      accent,
      accentInk,
      btn,
      infoChip: custom.infoChip,
    });
  }

  const accent = colorOr(theme.accent, preset.defaultAccent);
  const dark = preset.mode === 'dark';
  const accentInk = inkOn(accent);
  const btn = resolveButtonColors({
    style,
    dark,
    accent,
    accentInk,
    text: preset.text,
    muted: preset.muted,
  });

  return buildResolvedTheme({
    preset,
    mode: preset.mode,
    pageBg: preset.pageBg,
    cover: theme.cleanCover ? preset.cover : paletteCover(theme.palette, preset.cover),
    accent,
    accentInk,
    btn,
    infoChip: dark ? 'rgba(255,255,255,0.06)' : rgba(preset.text, 0.05),
  });
};
